import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Spinner, Alert, Input, InputGroup, InputGroupText,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label,
  UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { getAllConversations, getConversationMessages } from "../../../helpers/chatFirebase";
import { fetchIlanNames } from "../../../helpers/lookupNames";

// Türkçe-duyarsız arama için normalize.
const norm = (s) =>
  String(s || "").toLocaleLowerCase("tr-TR").replace(/ı/g, "i").replace(/i̇/g, "i");

// "YYYY/MM/DD H:mm:ss" → Date
const parseChatDate = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(/\//g, "-").replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
};

const relativeTime = (s) => {
  const d = parseChatDate(s);
  if (!d) return s || "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa`;
  if (diff < 172800) return "dün";
  if (diff < 604800) return `${Math.floor(diff / 86400)} gün`;
  return d.toLocaleDateString("tr-TR");
};

// Katılımcı adlarını "A ↔ B" biçiminde döndürür.
const partiNames = (s) =>
  Object.values(s.participants || {}).map((p) => p?.name).filter(Boolean);

// Metin içinde arama terimini <mark> ile vurgular.
const highlight = (text, term) => {
  if (!term) return text;
  const t = String(text || "");
  const nt = norm(t);
  const nq = norm(term);
  const parts = [];
  let idx = 0;
  let pos = nt.indexOf(nq);
  if (pos === -1) return t;
  while (pos !== -1) {
    parts.push(t.slice(idx, pos));
    parts.push(<mark key={idx} className="p-0">{t.slice(pos, pos + term.length)}</mark>);
    idx = pos + term.length;
    pos = nt.indexOf(nq, idx);
  }
  parts.push(t.slice(idx));
  return parts;
};

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

// Sohbetin bağlı olduğu ilanı İlan Yönetimi'nde detay modalı açık olarak getirir.
// IlanYonetimi bu parametreyi okuyup ilgili ilanın detayını açar.
const ilanLinki = (advertID) => `/ilan-yonetimi?ilan=${encodeURIComponent(advertID)}`;

const SIKAYET_TIPLERI = [
  "Uygunsuz İçerik",
  "Taciz / Hakaret",
  "Dolandırıcılık Şüphesi",
  "Spam / Reklam",
  "Diğer",
];

const Mesajlasmalar = () => {
  document.title = "Mesajlaşmalar | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const apiHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [sohbetler, setSohbetler] = useState([]);
  const [ilanMap, setIlanMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");          // sohbet listesi araması
  const [aktif, setAktif] = useState(null);           // seçili sohbet
  const [mesajlar, setMesajlar] = useState([]);
  const [mesajLoading, setMesajLoading] = useState(false);
  const [msgSearch, setMsgSearch] = useState("");     // sohbet içi mesaj araması

  // ─── Moderasyon ────────────────────────────────────────────────
  const [modOpen, setModOpen] = useState(false);
  const [modLoading, setModLoading] = useState(false);
  const [modMsg, setModMsg] = useState(null);         // {type,text} sonuç bildirimi
  const [sikayetTip, setSikayetTip] = useState(SIKAYET_TIPLERI[0]);
  const [sikayetNot, setSikayetNot] = useState("");
  const [pasifKullanicilar, setPasifKullanicilar] = useState({}); // {uid: true} bu oturumda pasife alınanlar

  const mesajEndRef = useRef(null);

  const loadSohbetler = async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await getAllConversations();
      setSohbetler(rows);
      const iMap = await fetchIlanNames(rows.map((r) => r.advertID));
      setIlanMap(iMap);
    } catch {
      setError("Sohbetler yüklenemedi. Firestore erişimi/izni kontrol edilmeli.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSohbetler(); }, []);

  const openSohbet = async (s) => {
    setAktif(s);
    setMesajlar([]);
    setMsgSearch("");
    setMesajLoading(true);
    try {
      const msgs = await getConversationMessages(s.id);
      setMesajlar(msgs);
    } catch {
      setError("Mesajlar yüklenemedi.");
    } finally {
      setMesajLoading(false);
    }
  };

  // Yeni sohbet açıldığında en alta kaydır.
  useEffect(() => {
    if (!mesajLoading && mesajlar.length && !msgSearch) {
      mesajEndRef.current?.scrollIntoView({ block: "end" });
    }
  }, [mesajLoading, mesajlar, msgSearch]);

  // ─── Sohbet listesi filtresi ───────────────────────────────────
  const filtreliSohbetler = useMemo(() => {
    if (!search.trim()) return sohbetler;
    const q = norm(search);
    return sohbetler.filter((s) => {
      const names = partiNames(s).join(" ");
      const phones = Object.values(s.participants || {}).map((p) => p?.phone).join(" ");
      const ilan = ilanMap[s.advertID] || "";
      return (
        norm(names).includes(q) ||
        norm(phones).includes(q) ||
        norm(s.lastMessageText).includes(q) ||
        norm(ilan).includes(q) ||
        norm(s.advertID).includes(q)
      );
    });
  }, [sohbetler, search, ilanMap]);

  // ─── Sohbet içi mesaj filtresi ─────────────────────────────────
  const filtreliMesajlar = useMemo(() => {
    if (!msgSearch.trim()) return mesajlar;
    const q = norm(msgSearch);
    return mesajlar.filter((m) => norm(m.customerMessage).includes(q));
  }, [mesajlar, msgSearch]);

  const toplamMesajBu = mesajlar.length;
  const eslesme = filtreliMesajlar.length;

  // ─── Moderasyon aksiyonları ────────────────────────────────────
  const flash = (type, text) => {
    setModMsg({ type, text });
    setTimeout(() => setModMsg(null), 4000);
  };

  // Sohbetin ilanına şikayet kaydı oluşturur (ILAN_SIKAYETLERI).
  const createSikayet = async () => {
    if (!aktif?.advertID) {
      flash("warning", "Bu sohbet bir ilana bağlı değil, şikayet oluşturulamıyor.");
      return;
    }
    setModLoading(true);
    try {
      // Sohbeti başlatan ilk katılımcıyı şikayet eden olarak kaydet (izlenebilirlik için).
      const ilkUid = (aktif.participantIds || [])[0];
      const payload = {
        ILAN_ID: Number(aktif.advertID),
        KULLANICI_ID: ilkUid ? Number(ilkUid) : null,
        SIKAYET_TIP: sikayetTip,
        ACIKLAMA: `[Sohbet Moderasyonu] ${sikayetNot || "—"} (Sohbet: ${aktif.id})`,
        DURUM: "Beklemede",
        TARIH: new Date().toISOString(),
      };
      const res = await fetch(`${BASE_URL}/ILAN_SIKAYETLERI`, {
        method: "POST", headers: apiHeaders, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      flash("success", "Şikayet kaydı oluşturuldu. Şikayet Yönetimi'nden takip edilebilir.");
      setModOpen(false);
      setSikayetNot("");
    } catch {
      flash("danger", "Şikayet oluşturulamadı. Yetki/bağlantı kontrol edilmeli.");
    } finally {
      setModLoading(false);
    }
  };

  // Bir katılımcıyı pasife alır (KULLANICILAR.AKTIF_DURUM = 'Pasif').
  const setKullaniciPasif = async (uid, isim) => {
    if (!uid) return;
    if (!window.confirm(`${isim || "Kullanıcı #" + uid} pasife alınsın mı? Kullanıcı uygulamaya giriş yapamayacak.`)) return;
    setModLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/KULLANICILAR(${Number(uid)})`, {
        method: "PATCH", headers: apiHeaders, body: JSON.stringify({ AKTIF_DURUM: "Pasif" }),
      });
      if (!res.ok) throw new Error();
      setPasifKullanicilar((p) => ({ ...p, [uid]: true }));
      flash("success", `${isim || "Kullanıcı"} pasife alındı.`);
    } catch {
      flash("danger", "İşlem başarısız. Yetki/bağlantı kontrol edilmeli.");
    } finally {
      setModLoading(false);
    }
  };

  // Bir katılımcıyı yeniden aktifleştirir (geri alma).
  const setKullaniciAktif = async (uid, isim) => {
    if (!uid) return;
    setModLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/KULLANICILAR(${Number(uid)})`, {
        method: "PATCH", headers: apiHeaders, body: JSON.stringify({ AKTIF_DURUM: "Aktif" }),
      });
      if (!res.ok) throw new Error();
      setPasifKullanicilar((p) => { const c = { ...p }; delete c[uid]; return c; });
      flash("success", `${isim || "Kullanıcı"} yeniden aktifleştirildi.`);
    } catch {
      flash("danger", "İşlem başarısız.");
    } finally {
      setModLoading(false);
    }
  };

  const avatar = (p, name) => {
    if (p?.photo) {
      return <img src={p.photo} alt="" className="rounded-circle avatar-xs" style={{ objectFit: "cover" }} />;
    }
    return (
      <div className="avatar-xs rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center">
        <span className="text-primary fw-bold font-size-11">{(name || "?").charAt(0).toLocaleUpperCase("tr-TR")}</span>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İçerik Yönetimi" breadcrumbItem="Mesajlaşmalar" />

          <Alert color="info" className="d-flex align-items-center py-2">
            <i className="bx bx-info-circle me-2 font-size-18" />
            <span className="font-size-13">
              Mobil uygulamadaki kullanıcı-kullanıcı sohbetleri <strong>salt-okunur</strong> olarak görüntülenir.
              Toplam <strong>{sohbetler.length}</strong> sohbet.
            </span>
            <Button color="info" size="sm" outline className="ms-auto" onClick={loadSohbetler} disabled={loading}>
              <i className="bx bx-refresh me-1" />Yenile
            </Button>
          </Alert>

          {error && (
            <Alert color="warning" className="d-flex align-items-center justify-content-between">
              <span><i className="mdi mdi-alert-outline me-2" />{error}</span>
              <Button color="warning" size="sm" outline onClick={loadSohbetler}>
                <i className="bx bx-refresh me-1" />Tekrar Dene
              </Button>
            </Alert>
          )}

          <Card>
            <CardBody className="p-0">
              <Row className="g-0">
                {/* ── SOL: SOHBET LİSTESİ ───────────────────────── */}
                <Col md={4} lg={4} className="border-end">
                  <div className="p-3 border-bottom">
                    <InputGroup>
                      <InputGroupText className="bg-light border-end-0">
                        <i className="bx bx-search" />
                      </InputGroupText>
                      <Input
                        placeholder="İsim, telefon, ilan veya mesaj ara..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="border-start-0"
                      />
                      {search && (
                        <Button color="light" onClick={() => setSearch("")}><i className="bx bx-x" /></Button>
                      )}
                    </InputGroup>
                    {search && (
                      <small className="text-muted d-block mt-1">{filtreliSohbetler.length} sonuç</small>
                    )}
                  </div>

                  <div style={{ height: 560, overflowY: "auto" }}>
                    {loading ? (
                      <div className="text-center py-5"><Spinner color="primary" /><p className="mt-2 text-muted">Yükleniyor...</p></div>
                    ) : filtreliSohbetler.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <i className="bx bx-chat display-4 opacity-25 d-block mb-2" />
                        {search ? "Eşleşen sohbet yok" : "Sohbet bulunamadı"}
                      </div>
                    ) : (
                      filtreliSohbetler.map((s) => {
                        const parts = s.participants || {};
                        const names = partiNames(s);
                        const isim = names.join(" ↔ ") || "Sohbet";
                        const firstUid = (s.participantIds || [])[0] || Object.keys(parts)[0];
                        const aktifMi = aktif?.id === s.id;
                        return (
                          <div
                            key={s.id}
                            onClick={() => openSohbet(s)}
                            className={`d-flex align-items-center gap-2 p-2 px-3 border-bottom ${aktifMi ? "bg-light" : ""}`}
                            style={{ cursor: "pointer" }}
                          >
                            {avatar(parts[firstUid], names[0])}
                            <div className="flex-grow-1 overflow-hidden">
                              <div className="d-flex justify-content-between">
                                <span className="fw-medium font-size-13 text-truncate">{highlight(isim, search)}</span>
                                <small className="text-muted flex-shrink-0 ms-1">{relativeTime(s.lastMessageAt)}</small>
                              </div>
                              <div className="text-muted font-size-12 text-truncate">
                                {highlight(s.lastMessageText || "—", search)}
                              </div>
                              {ilanMap[s.advertID] && (
                                <Link
                                  to={ilanLinki(s.advertID)}
                                  onClick={(e) => e.stopPropagation()}
                                  title="İlana git"
                                  className="d-inline-block mt-1 text-decoration-none"
                                >
                                  <Badge
                                    color="light"
                                    className="text-dark border font-size-10"
                                    style={{ cursor: "pointer" }}
                                  >
                                    <i className="bx bx-package me-1" />
                                    {highlight(ilanMap[s.advertID], search)}
                                  </Badge>
                                </Link>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </Col>

                {/* ── SAĞ: MESAJ AKIŞI ──────────────────────────── */}
                <Col md={8} lg={8}>
                  {!aktif ? (
                    <div className="d-flex flex-column justify-content-center align-items-center text-muted" style={{ height: 620 }}>
                      <i className="bx bx-message-rounded-dots display-3 opacity-25 mb-2" />
                      <p className="mb-0">Görüntülemek için soldan bir sohbet seçin.</p>
                    </div>
                  ) : (
                    <>
                      {modMsg && (
                        <Alert color={modMsg.type} className="m-2 mb-0 py-2 font-size-13">
                          {modMsg.text}
                        </Alert>
                      )}
                      {/* Başlık */}
                      <div className="p-3 border-bottom d-flex align-items-center gap-2">
                        <div className="flex-grow-1">
                          <div className="fw-bold font-size-14">{partiNames(aktif).join(" ↔ ") || "Sohbet"}</div>
                          <div className="text-muted font-size-12">
                            {Object.values(aktif.participants || {}).map((p) => p?.phone).filter(Boolean).join(" · ") || "—"}
                            {aktif.advertID && (
                              <>
                                {" · "}
                                <Link
                                  to={ilanLinki(aktif.advertID)}
                                  className="text-primary fw-medium"
                                  title="İlan detayını aç"
                                >
                                  <i className="bx bx-package me-1" />
                                  {ilanMap[aktif.advertID] || `İlan #${aktif.advertID}`}
                                  <i className="bx bx-link-external ms-1 font-size-11" />
                                </Link>
                              </>
                            )}
                          </div>
                        </div>
                        <Badge color="light" className="text-dark border">{toplamMesajBu} mesaj</Badge>
                        <UncontrolledDropdown>
                          <DropdownToggle color="light" size="sm" caret disabled={modLoading}>
                            {modLoading
                              ? <Spinner size="sm" />
                              : <><i className="bx bx-shield-quarter me-1" />Moderasyon</>}
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem header>Sohbet</DropdownItem>
                            <DropdownItem onClick={() => { setModMsg(null); setModOpen(true); }} disabled={!aktif.advertID}>
                              <i className="bx bx-flag text-danger me-2" />Şikayet Oluştur
                            </DropdownItem>
                            <DropdownItem divider />
                            <DropdownItem header>Kullanıcıyı Pasife Al</DropdownItem>
                            {(aktif.participantIds || Object.keys(aktif.participants || {})).map((uid) => {
                              const isim = aktif.participants?.[uid]?.name || `#${uid}`;
                              const pasif = pasifKullanicilar[uid];
                              return pasif ? (
                                <DropdownItem key={uid} onClick={() => setKullaniciAktif(uid, isim)}>
                                  <i className="bx bx-user-check text-success me-2" />{isim} → Aktifleştir
                                </DropdownItem>
                              ) : (
                                <DropdownItem key={uid} onClick={() => setKullaniciPasif(uid, isim)}>
                                  <i className="bx bx-user-x text-warning me-2" />{isim}
                                </DropdownItem>
                              );
                            })}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>

                      {/* Mesaj içi arama */}
                      <div className="p-2 px-3 border-bottom bg-light">
                        <InputGroup size="sm">
                          <InputGroupText className="bg-white border-end-0"><i className="bx bx-search-alt" /></InputGroupText>
                          <Input
                            className="border-start-0"
                            placeholder="Bu sohbette mesaj ara..."
                            value={msgSearch}
                            onChange={(e) => setMsgSearch(e.target.value)}
                          />
                          {msgSearch && (
                            <>
                              <InputGroupText className="bg-white text-muted">{eslesme} eşleşme</InputGroupText>
                              <Button color="light" onClick={() => setMsgSearch("")}><i className="bx bx-x" /></Button>
                            </>
                          )}
                        </InputGroup>
                      </div>

                      {/* Mesajlar */}
                      <div style={{ height: 500, overflowY: "auto", background: "#f5f6f8" }} className="p-3">
                        {mesajLoading ? (
                          <div className="text-center py-5"><Spinner color="primary" /></div>
                        ) : filtreliMesajlar.length === 0 ? (
                          <div className="text-center text-muted py-4">
                            {msgSearch ? "Eşleşen mesaj yok." : "Mesaj yok."}
                          </div>
                        ) : (
                          <>
                            {filtreliMesajlar.map((m) => {
                              // Mobil uygulama gönderen ID'sini `customerNumber` alanında tutar
                              // (senderId değil). Eski/olası varyantlara da düşülür.
                              const senderUid = m.customerNumber ?? m.senderId ?? m.customerId ?? "";
                              const ids = aktif.participantIds || Object.keys(aktif.participants || {});
                              // İlk katılımcı sağda (WhatsApp'taki "ben"), diğerleri solda.
                              const sag = String(senderUid) === String(ids[0]);
                              const gonderen =
                                aktif.participants?.[senderUid]?.name ||
                                (senderUid ? `#${senderUid}` : "Bilinmeyen");
                              return (
                                <div key={m.id} className={`d-flex mb-2 ${sag ? "justify-content-end" : "justify-content-start"}`}>
                                  <div
                                    className={`p-2 rounded shadow-sm ${sag ? "bg-primary text-white" : "bg-white border"}`}
                                    style={{ maxWidth: "75%" }}
                                  >
                                    <div className={`font-size-10 mb-1 ${sag ? "text-white-50" : "text-muted"}`}>{gonderen}</div>
                                    {m.type === "image" ? (
                                      <a href={m.customerMessage} target="_blank" rel="noreferrer">
                                        <img src={m.customerMessage} alt="mesaj görseli" style={{ maxWidth: "100%", borderRadius: 6 }} />
                                      </a>
                                    ) : m.type === "location" ? (
                                      <a
                                        href={`https://maps.google.com/?q=${m.lat},${m.lng}`}
                                        target="_blank" rel="noreferrer"
                                        className={sag ? "text-white" : ""}
                                      >
                                        <i className="bx bx-map me-1" />Konumu Görüntüle
                                      </a>
                                    ) : (
                                      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                        {highlight(m.customerMessage, msgSearch)}
                                      </div>
                                    )}
                                    <div className={`font-size-10 mt-1 text-end ${sag ? "text-white-50" : "text-muted"}`}>{m.messageDate}</div>
                                  </div>
                                </div>
                              );
                            })}
                            <div ref={mesajEndRef} />
                          </>
                        )}
                      </div>
                    </>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* ══ ŞİKAYET OLUŞTURMA MODAL ══════════════════════════════ */}
      <Modal isOpen={modOpen} toggle={() => setModOpen(false)} centered>
        <ModalHeader toggle={() => setModOpen(false)}>
          <i className="bx bx-flag text-danger me-2" />Sohbet Şikayeti Oluştur
        </ModalHeader>
        <ModalBody>
          {aktif && (
            <div className="bg-light rounded p-2 mb-3 font-size-13">
              <div><strong>Sohbet:</strong> {partiNames(aktif).join(" ↔ ") || aktif.id}</div>
              {aktif.advertID && (
                <div>
                  <strong>İlan:</strong>{" "}
                  <Link to={ilanLinki(aktif.advertID)} target="_blank" rel="noreferrer">
                    {ilanMap[aktif.advertID] || `İlan #${aktif.advertID}`} (#{aktif.advertID})
                  </Link>
                </div>
              )}
            </div>
          )}
          <FormGroup>
            <Label className="fw-bold">Şikayet Tipi</Label>
            <Input type="select" value={sikayetTip} onChange={(e) => setSikayetTip(e.target.value)}>
              {SIKAYET_TIPLERI.map((t) => <option key={t} value={t}>{t}</option>)}
            </Input>
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="fw-bold">Açıklama / Not</Label>
            <Input
              type="textarea" rows={4}
              placeholder="Şikayet gerekçesini yazın..."
              value={sikayetNot}
              onChange={(e) => setSikayetNot(e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModOpen(false)} disabled={modLoading}>İptal</Button>
          <Button color="danger" onClick={createSikayet} disabled={modLoading}>
            {modLoading ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-flag me-1" />}
            Şikayet Oluştur
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Mesajlasmalar;
